"use server";

import { redirect } from "next/navigation";
import { cookies } from "next/headers";
import { revalidatePath } from "next/cache";
import { DEMO_ACCOUNT, type LoginState } from "@/Helpers/DemoAccount";
import {
  DEMO_SESSION_SECONDS,
  getDemoSession,
  safeReturnTo,
} from "@/Helpers/DemoSession";
import { ApiError, AuthService, isApiConfigured } from "@/Services";
import { readLoginCredentials } from "@/Helpers/FormValidation";
import { isLocale } from "@/libs/localization/i18n";

export async function signIn(
  _previous: LoginState,
  formData: FormData,
): Promise<LoginState> {
  const credentials = readLoginCredentials(formData);
  if (!credentials) return { error: "invalidInput" };
  const { email, password } = credentials;
  const language = formData.get("locale");
  const locale =
    typeof language === "string" && isLocale(language) ? language : "nl";
  try {
    const session = await getDemoSession();
    if (isApiConfigured()) {
      const { data: result } = await AuthService.login(credentials, locale);
      session.userId = result.user.uuid;
      session.accessToken = result.token;
      session.expiresAt = result.expiresAt;
    } else {
      if (email !== DEMO_ACCOUNT.email || password !== DEMO_ACCOUNT.password)
        return { error: "invalidCredentials" };
      session.userId = DEMO_ACCOUNT.id;
      delete session.accessToken;
      session.expiresAt = Date.now() + DEMO_SESSION_SECONDS * 1000;
    }
    await session.save();
    (await cookies()).set("baken-locale", locale, {
      path: "/",
      maxAge: 31536000,
      sameSite: "lax",
    });
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 401) return { error: "invalidCredentials" };
      if (error.status === 422) return { error: "invalidInput" };
      if (error.status === 429) return { error: "rateLimited" };
    }
    return { error: "unavailable" };
  }
  revalidatePath("/", "layout");
  redirect(safeReturnTo(formData.get("next"), locale));
  return {};
}
export async function signOut(formData: FormData) {
  const language = formData.get("locale");
  const locale =
    typeof language === "string" && isLocale(language) ? language : "nl";
  const session = await getDemoSession();
  let revokeFailed = false;
  if (isApiConfigured() && session.accessToken) {
    try {
      await AuthService.logout(session.accessToken, locale);
    } catch (error) {
      revokeFailed = !(error instanceof ApiError && error.status === 401);
    }
  }
  session.destroy();
  revalidatePath("/", "layout");
  redirect(`/${locale}/login${revokeFailed ? "?logout=failed" : ""}`);
}
